import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Share2, CalendarClock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { buildJobPostingLd } from "../lib/jobPostingLd";

export default function JobDetailPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        axios
            .get(`${API}/jobs`)
            .then(({ data }) => {
                const found = (data.items || []).find((j) => j.slug === slug);
                if (found) setJob(found);
                else setNotFound(true);
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (!job) return;
        document.title = `${job.title} ${job.suffix || ""} – Möbus Umweltschutz GmbH`;
        const ld = buildJobPostingLd(job);
        let el = document.getElementById("job-detail-jsonld");
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = "job-detail-jsonld";
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(ld);
        return () => {
            document.title = "Möbus Umweltschutz GmbH – Karriere";
            document.getElementById("job-detail-jsonld")?.remove();
        };
    }, [job]);

    const share = async () => {
        const url = `${window.location.origin}/api/share/${slug}`;
        if (navigator.share) {
            try {
                await navigator.share({ title: document.title, url });
                return;
            } catch {
                return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link kopiert!");
        } catch (e) {
            console.error("JSON-LD injection failed:", e);
        }
    };

    if (notFound) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
                <h1 className="font-heading font-bold text-2xl text-moebus-deep">Stelle nicht gefunden</h1>
                <p className="text-slate-500">Diese Stelle ist nicht mehr verfügbar.</p>
                <Link to="/" className="text-moebus-green hover:underline inline-flex items-center gap-2">
                    <ArrowLeft size={16} /> Zu allen Stellen
                </Link>
            </div>
        );
    }

    if (!job) return <div className="min-h-screen bg-white" />;

    return (
        <div className="min-h-screen bg-white" data-testid="job-detail-page">
            <div className="bg-moebus-deep text-white py-16 md:py-20">
                <div className="container mx-auto px-4 md:px-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 mb-4"
                    >
                        <ArrowLeft size={16} /> Alle Stellen
                    </Link>
                    <div className="text-xs tracking-[0.3em] uppercase text-emerald-400 font-semibold mb-3">
                        Stellenangebot {job.suffix}
                    </div>
                    <h1 className="font-heading font-bold text-4xl sm:text-5xl leading-tight max-w-3xl">
                        {job.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-slate-300 text-sm">
                        <span className="inline-flex items-center gap-2">
                            <MapPin size={15} className="text-emerald-400" /> Neustadt in Sachsen · Vollzeit
                        </span>
                        {job.expiration_date && (
                            <span data-testid="job-expiration-date" className="inline-flex items-center gap-2">
                                <CalendarClock size={15} className="text-emerald-400" />
                                Bewerbungsschluss: {new Date(`${job.expiration_date}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
                <div className="max-w-3xl mx-auto space-y-8">
                    <p className="text-base md:text-lg text-slate-700 leading-relaxed">{job.description}</p>

                    {job.tasks?.length > 0 && (
                        <div>
                            <h2 className="font-heading font-bold text-xl md:text-2xl text-moebus-deep mb-4">
                                Deine Aufgaben
                            </h2>
                            <ul className="space-y-2.5">
                                {job.tasks.map((t) => (
                                    <li key={t} className="flex gap-2.5 text-slate-700">
                                        <CheckCircle2 size={18} className="text-moebus-green flex-shrink-0 mt-0.5" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {job.profile?.length > 0 && (
                        <div>
                            <h2 className="font-heading font-bold text-xl md:text-2xl text-moebus-deep mb-4">
                                Dein Profil
                            </h2>
                            <ul className="space-y-2.5">
                                {job.profile.map((t) => (
                                    <li key={t} className="flex gap-2.5 text-slate-700">
                                        <CheckCircle2 size={18} className="text-moebus-blue flex-shrink-0 mt-0.5" />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            data-testid="job-detail-apply"
                            onClick={() => navigate(`/?stelle=${slug}&bewerben=1`)}
                            className="inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-8 py-3.5 rounded-md transition-colors"
                        >
                            Jetzt auf diese Stelle bewerben <ArrowRight size={16} />
                        </button>
                        <button
                            data-testid="job-detail-share"
                            onClick={share}
                            className="inline-flex items-center justify-center gap-2 bg-white border border-moebus-border text-slate-700 px-6 py-3.5 rounded-md hover:bg-slate-50"
                        >
                            <Share2 size={16} /> Stelle teilen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
